// @file Vuex Store for Privacy Settings
import type { JsonAPIResource, JsonAPIResponse } from '@padlet/arvo'
import { fetchJson, HTTPMethod } from '@padlet/fetch'
import getCsrfToken from 'bits/csrf_token'
import { captureFetchException } from 'bits/error_tracker'
import { isAppUsing } from 'bits/flip'
import { __ } from 'bits/intl'
import { WallAccessPrivacyType, WallAccessRight, WallCollaboratorStatus } from 'enums'
import { cloneDeep, debounce } from 'lodash-es'
import PadletApi from 'surface/padlet_api'
import type {
  PrivacyPolicyId,
  UserGroupWallCollaborator,
  UserId,
  UserWallCollaborator,
  WallAccessSettings,
} from 'types'
import type { Module } from 'vuex'

interface WallAccessSettingsState {
  wallAccessSettings: WallAccessSettings
  originalWallAccessSettings: WallAccessSettings | null
  errorMessage: string
  errorMessageTimeout: number | null
  isSaving: boolean
  isSearchingCollaborators: boolean
  collaboratorsSearchQuery: string
  userWallCollaboratorsSearchResult: UserWallCollaborator[]
  userGroupWallCollaboratorsSearchResult: UserGroupWallCollaborator[]
  debouncedSearchCollaboratorsFunction: Function | null
}

const buildUpdateWallAccessSettingsPayload = (wallAccessSettings: WallAccessSettings): Partial<WallAccessSettings> => {
  const updateWallAccessSettingsPayload = { ...wallAccessSettings }
  if (updateWallAccessSettingsPayload.privacyType !== WallAccessPrivacyType.PasswordProtected) {
    updateWallAccessSettingsPayload.password = null
  }
  return updateWallAccessSettingsPayload
}

const validateWallAccessSettings = (
  wallAccessSettings: WallAccessSettings,
): {
  isValid: boolean
  errorMessage?: string
} => {
  if (wallAccessSettings.privacyType === WallAccessPrivacyType.PasswordProtected) {
    if (!wallAccessSettings.password || wallAccessSettings.password.trim().length === 0) {
      return { isValid: false, errorMessage: __('You have to set a password!') }
    }
  }
  // This will happen if users select "Org only"/"Team only" as the privacy policy, then set
  // the permission to "Can administer", and then change the privacy policy to
  // something else. We should improve the UI in the future so this doesn't happen.
  if (
    wallAccessSettings.right === WallAccessRight.Administer &&
    wallAccessSettings.privacyType !== WallAccessPrivacyType.OrgWideUnlisted &&
    wallAccessSettings.privacyType !== WallAccessPrivacyType.OrgWideListed &&
    wallAccessSettings.privacyType !== WallAccessPrivacyType.LibraryWideUnlisted &&
    wallAccessSettings.privacyType !== WallAccessPrivacyType.LibraryWideListed
  ) {
    return {
      isValid: false,
      errorMessage: __('This privacy setting does not allow all viewers to administer the padlet. Please try another.'),
    }
  }
  return { isValid: true }
}

const findCollaboratorIndexInArray = (
  collaborator: Partial<UserWallCollaborator>,
  collaborators: UserWallCollaborator[],
): number => {
  return collaborators.findIndex(
    (c) =>
      (collaborator.email && c.email && collaborator.email === c.email) ||
      (collaborator.id && c.id && collaborator.id === c.id) ||
      (collaborator.username && c.username && collaborator.username === c.username),
  )
}

const haveBaseWallAccessSettingsChanged = (
  wallAccessSettings: WallAccessSettings,
  originalWallAccessSettings: WallAccessSettings,
): boolean => {
  if (!originalWallAccessSettings) return false
  for (const attribute of ['password', 'privacyType', 'right']) {
    if (wallAccessSettings[attribute] !== originalWallAccessSettings[attribute]) return true
  }
  return false
}

const haveCollaboratorsChanged = (
  wallAccessSettings: WallAccessSettings,
  originalWallAccessSettings: WallAccessSettings,
): boolean => {
  if (!originalWallAccessSettings) return false
  if (wallAccessSettings.collaborators.length !== originalWallAccessSettings.collaborators.length) return true
  for (let i = 0; i < wallAccessSettings.collaborators.length; i++) {
    const originalCollaboratorIndex = findCollaboratorIndexInArray(
      wallAccessSettings.collaborators[i],
      originalWallAccessSettings.collaborators,
    )
    if (originalCollaboratorIndex === -1) return true
    if (
      wallAccessSettings.collaborators[i].right !==
      originalWallAccessSettings.collaborators[originalCollaboratorIndex].right
    )
      return true
  }
  return false
}

// Keep this in sync with backend
const sanitizeCollaboratorsSearchQuery = (query): string => query.trim().replace(/[/\\()[\]{}*"'~:#&|!^?]/g, '')

const initialState = (): WallAccessSettingsState => {
  return {
    wallAccessSettings: {
      privacyType: WallAccessPrivacyType.Private,
      right: WallAccessRight.None,
      password: null,
      collaborators: [],
    },
    originalWallAccessSettings: null,
    errorMessage: '',
    errorMessageTimeout: null,
    isSaving: false,
    isSearchingCollaborators: false,
    collaboratorsSearchQuery: '',
    userWallCollaboratorsSearchResult: [],
    userGroupWallCollaboratorsSearchResult: [],
    debouncedSearchCollaboratorsFunction: null,
  }
}

const WallAccessSettingsModule: Module<WallAccessSettingsState, object> = {
  namespaced: true,
  state: initialState(),
  getters: {
    wallAccessSettings: (state): WallAccessSettings => state.wallAccessSettings,
    originalWallAccessSettings: (state): WallAccessSettings | null => state.originalWallAccessSettings,
    privacyPolicyId: (state): PrivacyPolicyId | undefined => state.wallAccessSettings.privacyPolicyId,
    privacyType: (state): WallAccessPrivacyType => state.wallAccessSettings.privacyType,
    right: (state): WallAccessRight => state.wallAccessSettings.right,
    password: (state): string | null => state.wallAccessSettings.password,
    collaborators: (state): UserWallCollaborator[] => state.wallAccessSettings.collaborators,
    originalCollaborators: (state): UserWallCollaborator[] =>
      state.originalWallAccessSettings != null ? state.originalWallAccessSettings.collaborators : [],
    isPasswordSet: (_, getters): boolean => !!getters.password,
    isWallAccessSettingsFetched: (state): boolean => !!state.wallAccessSettings.wallId,
    visitorsCanModerate(_, getters): boolean {
      return (
        getters.isWallAccessSettingsFetched &&
        [WallAccessRight.Edit, WallAccessRight.Administer].includes(getters.right)
      )
    },
    isPrivacyTypeLoggedInUsersOnly: (state, getters): boolean =>
      getters.isWallAccessSettingsFetched &&
      state.wallAccessSettings.privacyType === WallAccessPrivacyType.LoggedInUsersOnly,
    isPrivacyTypeOrgWide: (state, getters): boolean =>
      getters.isWallAccessSettingsFetched &&
      (state.wallAccessSettings.privacyType === WallAccessPrivacyType.OrgWideUnlisted ||
        state.wallAccessSettings.privacyType === WallAccessPrivacyType.OrgWideListed),
    isPrivacyTypePasswordProtected: (state, getters): boolean =>
      getters.isWallAccessSettingsFetched &&
      state.wallAccessSettings.privacyType === WallAccessPrivacyType.PasswordProtected,
    isPrivacyTypePrivate: (state, getters): boolean =>
      getters.isWallAccessSettingsFetched && state.wallAccessSettings.privacyType === WallAccessPrivacyType.Private,
    isPrivacyTypeLibraryWide: (state, getters): boolean =>
      getters.isWallAccessSettingsFetched &&
      (state.wallAccessSettings.privacyType === WallAccessPrivacyType.LibraryWideUnlisted ||
        state.wallAccessSettings.privacyType === WallAccessPrivacyType.LibraryWideListed),
    errorMessage: (state): string => state.errorMessage,
    errorMessageTimeout: (state): number | null => state.errorMessageTimeout,
    isSaving: (state): boolean => state.isSaving,
    areBaseWallAccessSettingsDirty: (_, getters): boolean => {
      return haveBaseWallAccessSettingsChanged(getters.wallAccessSettings, getters.originalWallAccessSettings)
    },
    areCollaboratorsDirty: (_, getters): boolean => {
      return haveCollaboratorsChanged(getters.wallAccessSettings, getters.originalWallAccessSettings)
    },
    isCollaboratorAlreadyAdded:
      (_, getters) =>
      (collaborator: Partial<UserWallCollaborator>): boolean =>
        findCollaboratorIndexInArray(collaborator, getters.collaborators) !== -1,
    isCollaboratorAlreadyCommitted:
      (_, getters) =>
      (collaborator: Partial<UserWallCollaborator>): boolean =>
        findCollaboratorIndexInArray(collaborator, getters.originalCollaborators) !== -1,
    isCollaboratorAlreadyInvited:
      (_, getters) =>
      (collaborator: Partial<UserWallCollaborator>): boolean => {
        return getters.isCollaboratorAlreadyAdded(collaborator) && getters.isCollaboratorAlreadyCommitted(collaborator)
      },
    isCollaboratorInInvitedStatus:
      () =>
      (collaborator: Partial<UserWallCollaborator>): boolean => {
        return collaborator.status === WallCollaboratorStatus.Invited
      },
    invitedCollaborators: (state, getters): UserWallCollaborator[] =>
      state.wallAccessSettings.collaborators.filter((collaborator) =>
        getters.isCollaboratorInInvitedStatus(collaborator),
      ),
    approvedCollaborators: (state): UserWallCollaborator[] =>
      state.wallAccessSettings.collaborators.filter(
        (collaborator) => collaborator.status === WallCollaboratorStatus.Approved,
      ),
    collaboratorsSearchQuery: (state): string => state.collaboratorsSearchQuery,
    sanitizedCollaboratorsSearchQuery: (state): string =>
      sanitizeCollaboratorsSearchQuery(state.collaboratorsSearchQuery),
    isSearchingCollaborators: (state): boolean => state.isSearchingCollaborators,
    userWallCollaboratorsSearchResult: (state): UserWallCollaborator[] => state.userWallCollaboratorsSearchResult,
    userGroupWallCollaboratorsSearchResult: (state): UserGroupWallCollaborator[] =>
      state.userGroupWallCollaboratorsSearchResult,
    debouncedSearchCollaboratorsFunction: (state): Function | null => state.debouncedSearchCollaboratorsFunction,
  },
  mutations: {
    SET_WALL_ACCESS_SETTINGS(state, wallAccessSettings: WallAccessSettings): void {
      state.wallAccessSettings = wallAccessSettings
      state.originalWallAccessSettings = cloneDeep(wallAccessSettings)
    },
    SET_PRIVACY_TYPE(state, privacyType: WallAccessPrivacyType): void {
      state.wallAccessSettings = Object.assign({}, state.wallAccessSettings, {
        ...state.wallAccessSettings,
        privacyType,
      })
    },
    SET_PASSWORD(state, password: string): void {
      state.wallAccessSettings = Object.assign({}, state.wallAccessSettings, {
        ...state.wallAccessSettings,
        password,
      })
    },
    SET_RIGHT(state, right: WallAccessRight): void {
      state.wallAccessSettings = Object.assign({}, state.wallAccessSettings, {
        ...state.wallAccessSettings,
        right,
      })
    },
    SET_ERROR_MESSAGE(state, errorMessage: string): void {
      state.errorMessage = errorMessage
    },
    SET_ERROR_MESSAGE_TIMEOUT(state, errorMessageTimeout: number): void {
      state.errorMessageTimeout = errorMessageTimeout
    },
    SET_IS_SAVING(state, isSaving: boolean): void {
      state.isSaving = isSaving
    },
    EMPTY_WALL_ACCESS_SETTINGS(state): void {
      Object.assign(state, initialState())
      state.debouncedSearchCollaboratorsFunction = null
    },
    DISCARD_WALL_ACCESS_SETTINGS_CHANGES(state): void {
      if (state.originalWallAccessSettings == null) return
      state.wallAccessSettings = cloneDeep(state.originalWallAccessSettings)
      state.errorMessage = ''
      state.errorMessageTimeout = null
      state.isSaving = false
    },
    SET_COLLABORATOR_RIGHT(
      state,
      { collaborator, right }: { collaborator: UserWallCollaborator; right: WallAccessRight },
    ): void {
      const collaborators = state.wallAccessSettings.collaborators
      const collaboratorIndex = findCollaboratorIndexInArray(collaborator, collaborators)
      const newCollaborator = { ...collaborator, right }
      collaborators.splice(collaboratorIndex, 1, newCollaborator)
    },
    REMOVE_COLLABORATOR(state, collaborator: UserWallCollaborator): void {
      const collaborators = state.wallAccessSettings.collaborators
      const collaboratorIndex = findCollaboratorIndexInArray(collaborator, collaborators)
      collaborators.splice(collaboratorIndex, 1)
    },
    REMOVE_COLLABORATOR_REMOTELY(state, userId: UserId): void {
      if (state.originalWallAccessSettings == null) return
      const originalCollaborators = state.originalWallAccessSettings.collaborators
      const originalCollaboratorIndex = findCollaboratorIndexInArray({ id: userId }, originalCollaborators)
      originalCollaborators.splice(originalCollaboratorIndex, 1)
      const collaborators = state.wallAccessSettings.collaborators
      const collaboratorIndex = findCollaboratorIndexInArray({ id: userId }, collaborators)
      collaborators.splice(collaboratorIndex, 1)
    },
    SET_IS_SEARCHING_COLLABORATORS(state, isSearchingCollaborators: boolean): void {
      state.isSearchingCollaborators = isSearchingCollaborators
    },
    SET_COLLABORATORS_SEARCH_QUERY(state, collaboratorsSearchQuery: string): void {
      state.collaboratorsSearchQuery = collaboratorsSearchQuery
    },
    SET_USER_WALL_COLLABORATORS_SEARCH_RESULT(state, userWallCollaboratorsSearchResult: UserWallCollaborator[]): void {
      state.userWallCollaboratorsSearchResult = userWallCollaboratorsSearchResult
    },
    SET_USER_GROUP_WALL_COLLABORATORS_SEARCH_RESULT(
      state,
      userGroupWallCollaboratorsSearchResult: UserGroupWallCollaborator[],
    ): void {
      state.userGroupWallCollaboratorsSearchResult = userGroupWallCollaboratorsSearchResult
    },
    SET_DEBOUNCED_SEARCH_COLLABORATORS_FUNCTION(state, debouncedSearchCollaboratorsFunction: Function): void {
      state.debouncedSearchCollaboratorsFunction = debouncedSearchCollaboratorsFunction
    },
    STOP_COLLABORATORS_SEARCH(state): void {
      state.isSearchingCollaborators = false
      state.userWallCollaboratorsSearchResult = []
      state.collaboratorsSearchQuery = ''
    },
    ADD_COLLABORATOR(state, collaborator): void {
      state.wallAccessSettings.collaborators.push(collaborator)
    },
  },
  actions: {
    async fetchWallAccessSettings({ commit, rootGetters }): Promise<void> {
      try {
        const response: JsonAPIResponse<WallAccessSettings> = await fetchJson(
          `/api/6/walls/${rootGetters.wallId}/access_settings`,
          {
            method: HTTPMethod.get,
          },
        )
        const wallAccessSettings = (response?.data as JsonAPIResource<WallAccessSettings>)?.attributes
        commit('SET_WALL_ACCESS_SETTINGS', wallAccessSettings)
      } catch (e) {
        captureFetchException(e, { source: 'WallAccessSettingsFetch' })
      }
    },
    async updateWallAccessSettings({ commit, dispatch, getters, rootGetters }): Promise<void> {
      const { isValid, errorMessage } = validateWallAccessSettings(getters.wallAccessSettings)
      if (!isValid) {
        dispatch('setErrorMessage', { errorMessage })
        return
      }
      commit('SET_ERROR_MESSAGE', '')
      commit('SET_IS_SAVING', true)
      try {
        const payload = buildUpdateWallAccessSettingsPayload(getters.wallAccessSettings)
        const response: JsonAPIResponse<WallAccessSettings> = await fetchJson(
          `/api/6/walls/${rootGetters.wallId}/access_settings`,
          {
            method: HTTPMethod.patch,
            csrfToken: getCsrfToken(),
            jsonData: {
              data: {
                attributes: payload,
              },
            },
          },
        )
        const wallAccessSettings = (response?.data as JsonAPIResource<WallAccessSettings>)?.attributes
        commit('SET_WALL_ACCESS_SETTINGS', wallAccessSettings)
      } catch (e) {
        dispatch('setErrorMessage', { errorMessage: __('Sorry! Something went wrong. Please try saving again.') })
      } finally {
        commit('SET_IS_SAVING', false)
      }
    },
    setPrivacyType({ commit, getters, rootGetters }, { privacyType }: { privacyType: WallAccessPrivacyType }): void {
      if (getters.privacyType === WallAccessPrivacyType.Private) {
        commit('SET_RIGHT', WallAccessRight.Read)
      }
      if (privacyType === WallAccessPrivacyType.Private) {
        commit('SET_RIGHT', WallAccessRight.None)
      }

      if (
        privacyType === WallAccessPrivacyType.OrgWideUnlisted &&
        !(rootGetters.canIMakeOrgWideHiddenWalls as boolean) &&
        (rootGetters.canIMakeOrgWideDashboardWalls as boolean)
      ) {
        commit('SET_PRIVACY_TYPE', WallAccessPrivacyType.OrgWideListed)
        return
      }

      commit('SET_PRIVACY_TYPE', privacyType)
    },
    setPassword({ commit }, { password }: { password: string }): void {
      commit('SET_PASSWORD', password)
    },
    setRight({ commit }, { right }: { right: WallAccessRight }): void {
      commit('SET_RIGHT', right)
    },
    setErrorMessage({ commit, getters }, { errorMessage }: { errorMessage: string }): void {
      getters.errorMessageTimeout && window.clearTimeout(getters.errorMessageTimeout)
      commit('SET_ERROR_MESSAGE', errorMessage)
      commit(
        'SET_ERROR_MESSAGE_TIMEOUT',
        window.setTimeout(() => {
          commit('SET_ERROR_MESSAGE', '')
        }, 5000),
      )
    },
    emptyWallAccessSettings({ commit }): void {
      commit('EMPTY_WALL_ACCESS_SETTINGS')
    },
    discardChanges({ commit }): void {
      commit('DISCARD_WALL_ACCESS_SETTINGS_CHANGES')
    },
    setCollaboratorRight(
      { commit, getters },
      { collaborator, right }: { collaborator: UserWallCollaborator; right: WallAccessRight },
    ): void {
      if (!getters.isCollaboratorAlreadyAdded(collaborator)) return
      commit('SET_COLLABORATOR_RIGHT', { collaborator, right })
    },
    removeCollaborator({ commit }, { collaborator }: { collaborator: UserWallCollaborator }): void {
      commit('REMOVE_COLLABORATOR', collaborator)
    },
    removeCollaboratorRemotely(
      { commit, dispatch, getters, rootGetters },
      { userId }: { userId: UserId | null },
    ): void {
      if (!userId) return
      if (getters.isWallAccessSettingsFetched) {
        commit('REMOVE_COLLABORATOR_REMOTELY', userId)
      }
      if (rootGetters.isApp) {
        // Put the user id inside postSurfaceState's transientPayload so it's only sent once.
        // Sending the same id multiple time may break things if the user if re-added after they left the padlet.
        dispatch('nativeApp/postSurfaceState', { collaborator_just_left_user_id: userId }, { root: true })
      }
    },
    async searchCollaborators({ dispatch, commit, getters, rootGetters }, { query }): Promise<void> {
      commit('SET_IS_SEARCHING_COLLABORATORS', true)
      try {
        if (isAppUsing('userGroups')) {
          const response = await PadletApi.WallCollaborator.search({
            searchQuery: encodeURIComponent(query),
            wallId: rootGetters.wallId,
          })

          // Filter group collaborators
          const userGroupWallCollaborators = (response.data as Array<JsonAPIResource<UserGroupWallCollaborator>>)
            .filter(
              (collaborator: JsonAPIResource<UserGroupWallCollaborator>) =>
                collaborator.type === 'userGroupCollaborator',
            )
            .map((collaborator) => collaborator.attributes)
          const filteredUserGroupWallCollaborators = userGroupWallCollaborators.filter(
            (collaborator) =>
              getters.isCollaboratorAlreadyAdded(collaborator) === false &&
              !(collaborator.id !== null && collaborator.name === ''),
          )
          commit(
            'SET_USER_GROUP_WALL_COLLABORATORS_SEARCH_RESULT',
            filteredUserGroupWallCollaborators.map(
              (collaborator: UserGroupWallCollaborator): UserGroupWallCollaborator => {
                return {
                  ...collaborator,
                  right: getters.right !== WallAccessRight.None ? getters.right : WallAccessRight.Read,
                  status: WallCollaboratorStatus.Invited,
                }
              },
            ),
          )

          // Filter user collaborators
          const userWallCollaborators = (response.data as Array<JsonAPIResource<UserWallCollaborator>>)
            .filter((collaborator) => collaborator.type === 'userCollaborator')
            .map((collaborator) => collaborator.attributes)
          const filteredUserWallCollaborators = userWallCollaborators.filter(
            (collaborator) =>
              getters.isCollaboratorAlreadyAdded(collaborator) === false &&
              !(collaborator.id !== null && collaborator.username === ''),
          )
          commit(
            'SET_USER_WALL_COLLABORATORS_SEARCH_RESULT',
            filteredUserWallCollaborators.map((collaborator: UserWallCollaborator): UserWallCollaborator => {
              return {
                ...collaborator,
                right: getters.right !== WallAccessRight.None ? getters.right : WallAccessRight.Read,
                status: WallCollaboratorStatus.Invited,
              }
            }),
          )
        } else {
          const response: UserWallCollaborator[] = await fetchJson(
            `/search/collaborators?q=${encodeURIComponent(query)}&limit=5`,
            {
              method: HTTPMethod.get,
            },
          )
          const filteredCollaborators = response.filter(
            (collaborator) =>
              !getters.isCollaboratorAlreadyAdded(collaborator) && !(collaborator.id && !collaborator.username),
          )
          commit(
            'SET_USER_WALL_COLLABORATORS_SEARCH_RESULT',
            filteredCollaborators.map((collaborator: UserWallCollaborator): UserWallCollaborator => {
              return {
                ...collaborator,
                right: getters.right !== WallAccessRight.None ? getters.right : WallAccessRight.Read,
                status: WallCollaboratorStatus.Invited,
              }
            }),
          )
        }
      } catch (error) {
        dispatch('setErrorMessage', { errorMessage: __('Sorry! Something went wrong. Please try searching again.') })
      } finally {
        commit('SET_IS_SEARCHING_COLLABORATORS', false)
      }
    },
    debouncedSearchCollaborators({ getters, commit, dispatch }, { query }: { query: string }): void {
      commit('SET_COLLABORATORS_SEARCH_QUERY', query)
      const sanitizedQuery = getters.sanitizedCollaboratorsSearchQuery
      if (sanitizedQuery.length <= 0) {
        commit('STOP_COLLABORATORS_SEARCH')
      } else {
        if (!getters.debouncedSearchCollaboratorsFunction) {
          const dispatchSearch = async ({ query }): Promise<void> => await dispatch('searchCollaborators', { query })
          commit('SET_DEBOUNCED_SEARCH_COLLABORATORS_FUNCTION', debounce(dispatchSearch, 250))
        }
        getters.debouncedSearchCollaboratorsFunction({ query: sanitizedQuery })
      }
    },
    addCollaborator({ commit, getters }, { collaborator }: { collaborator: UserWallCollaborator }): void {
      if (getters.isCollaboratorAlreadyAdded(collaborator)) return
      commit('ADD_COLLABORATOR', collaborator)
      commit('STOP_COLLABORATORS_SEARCH')
    },
    addCollaboratorByEmail({ commit, getters }, { email }: { email: string }): void {
      const collaborator: Partial<UserWallCollaborator> = {
        email,
        right: getters.right !== WallAccessRight.None ? getters.right : WallAccessRight.Read,
        status: WallCollaboratorStatus.Invited,
      }
      commit('ADD_COLLABORATOR', collaborator)
      commit('STOP_COLLABORATORS_SEARCH')
    },
    async resendInvitation({ dispatch, getters }, { collaborator }): Promise<void> {
      if (!getters.privacyPolicyId) return
      try {
        await fetchJson(`/privacy_policies/${getters.privacyPolicyId}/reinvite`, {
          method: HTTPMethod.post,
          csrfToken: getCsrfToken(),
          jsonData: { invite_user_id: collaborator.id },
        })
      } catch (error) {
        dispatch('setErrorMessage', { errorMessage: __('Sorry! Something went wrong. Please try again.') })
      }
    },
  },
}

export default WallAccessSettingsModule
export type { WallAccessSettingsState }
